/**
 * Investment balances - dark_13. One place for the sum, so every screen
 * (Invest, Worth, Home, the charts, the money plan, the year review, the
 * widgets and the export) shows the same number.
 *
 * A manual balance is what the account held on the day you entered it.
 * Anything you add or take out after that day goes on top, and it carries
 * into the next months until you enter a new one. So October ends where it
 * really ended, and November starts from there.
 *
 * Manual balances saved before dark_13 have no date. They count as the end
 * of their month, so nothing is counted twice.
 */
const num = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
const pad = (n) => String(n).padStart(2, '0');

/** A date as 'YYYY-MM-DD' in the phone's own time zone. */
export const localDay = (d) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());

/** The last day of a 'YYYY-MM' month, as 'YYYY-MM-DD'. */
export const monthEndDay = (monthKey) => {
  const [y, m] = String(monthKey).split('-').map(Number);
  return monthKey + '-' + pad(new Date(y, m, 0).getDate());
};

/** The day a manual balance counts from, for a balance entered now for that month. */
export const manualDayFor = (monthKey, now = new Date()) => {
  const today = localDay(now);
  const current = today.slice(0, 7);
  if (monthKey < current) return monthEndDay(monthKey);
  if (monthKey > current) return monthKey + '-01';
  return today;
};

export const txDay = (t) => {
  if (!t || !t.date) return null;
  const s = String(t.date);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  return isNaN(d) ? s.slice(0, 10) : localDay(d);
};
const txMonth = (t) => t.month_key || (txDay(t) || '').slice(0, 7);

// What a transaction does to this account: + in, - out, 0 not this account.
const flowOf = (t, id) => {
  const sid = String(id);
  if (String(t.account_id) === sid && t.account_type === 'investment') {
    if (t.transaction_type === 'deposit') return num(t.amount);
    if (t.transaction_type === 'withdrawal') return -num(t.amount);
  }
  if (t.is_interest && String(t.interest_account_id) === sid && t.interest_account_type === 'investment') return num(t.amount);
  return 0;
};

/** The latest manual balance in or before a month: { month, value, at } or null. */
export function latestManual(inv, monthKey) {
  const ov = (inv && inv.override_by_month) || {};
  const keys = Object.keys(ov)
    .filter((k) => k <= monthKey && ov[k] !== undefined && ov[k] !== null && ov[k] !== '')
    .sort();
  if (!keys.length) return null;
  const month = keys[keys.length - 1];
  const at = ((inv.override_at_by_month || {})[month]) || null;
  return { month, value: num(ov[month]), at };
}

/**
 * The balance of an investment account for a month.
 *   opts.cutoffDay 'YYYY-MM-DD' - count transactions by date up to that day
 *   opts.all - count every transaction (a balance for "now")
 *   otherwise - count transactions in or before monthKey
 *   opts.ignoreManual - the plain calculated balance
 */
export function investmentBalance(inv, transactions, monthKey, opts = {}) {
  if (!inv) return 0;
  const inRange = (t) => {
    if (opts.all) return true;
    if (opts.cutoffDay) { const d = txDay(t); return !!d && d <= opts.cutoffDay; }
    return txMonth(t) <= monthKey;
  };
  const m = opts.ignoreManual ? null : latestManual(inv, monthKey);
  let total = m ? m.value : num(inv.starting_balance != null ? inv.starting_balance : inv.initial_balance);
  for (const t of transactions || []) {
    const f = flowOf(t, inv.id);
    if (!f || !inRange(t)) continue;
    if (m) {
      const k = txMonth(t);
      if (k < m.month) continue;
      if (k === m.month) {
        if (!m.at) continue; // an older manual balance counts as the end of its month
        const d = txDay(t);
        if (!d || d <= m.at) continue;
      }
    }
    total += f;
  }
  return total;
}

/** What has gone in or out since the latest manual balance (for the account page). */
export function changeSinceManual(inv, transactions, monthKey, opts = {}) {
  const m = latestManual(inv, monthKey);
  if (!m) return 0;
  return investmentBalance(inv, transactions, monthKey, opts) - m.value;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "Oct 3", or "Oct" for an older manual balance with no day. long: "October 3" / "in October". */
export function manualUpdatedLabel(m, long = false) {
  if (!m) return '';
  const [, mo] = m.month.split('-').map(Number);
  if (m.at) {
    const [, am, ad] = m.at.split('-').map(Number);
    return (long ? MONTHS_LONG : MONTHS)[am - 1] + ' ' + ad;
  }
  return long ? 'in ' + MONTHS_LONG[mo - 1] : MONTHS[mo - 1];
}
